import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, X, Save, Wifi, Tv, Wind, Waves, Car, Coffee, Utensils, Dumbbell } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuthState } from "@/hooks/useAuthState";
import { PropertyService } from "@/services/propertyService";
import { PropertyPhotoService } from "@/services/propertyPhotoService";
import { DevelopmentService, Development } from "@/services/developmentService";
import { Property, PropertyPhoto } from "@/integrations/mysql/types";
import { getPropertyImageUrl } from "@/config/api";
import { MinhaAreaPageShell } from "@/components/MinhaAreaPageShell";
import RichTextEditor from "@/components/RichTextEditor";
import {
  coverMatchesPhoto,
  extractImageFilename,
  getPhotoFilename,
} from "@/lib/property-image-filename";
import { persistPropertyPhotoToApi } from "@/lib/property-photos";

type PropertyFoto = {
  id: number | null;
  url: string;
  legenda: string;
  filename: string;
  /** Apenas cadastro novo: arquivo ainda não enviado à API */
  file?: File;
  pendingUpload?: boolean;
};

const amenidades = [
  { id: "Wifi", label: "Wifi", icon: Wifi },
  { id: "TV", label: "TV", icon: Tv },
  { id: "Piscina Compartilhada", label: "Piscina Compartilhada", icon: Waves },
  { id: "Estacionamento", label: "Estacionamento", icon: Car },
  { id: "Ar Condicionado", label: "Ar Condicionado", icon: Wind },
  { id: "Acesso a Praia", label: "Acesso a Praia", icon: Waves },
  { id: "Academia Compartilhada", label: "Academia Compartilhada", icon: Dumbbell },
  { id: "Parque Infantil ao Ar Livre", label: "Parque Infantil ao Ar Livre", icon: Utensils },
];

const NewProperty = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { id } = useParams<{ id: string }>();
  const { user, loading: authLoading, isAuthenticated, userType, userId, subscriptionPlan } = useAuthState();
  
  // Função para obter usuário de forma robusta
  const getCurrentUser = () => {
    // Primeiro, tentar usar o hook
    if (user && user.id) {
      return user;
    }
    
    // Fallback: obter do localStorage
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const userData = JSON.parse(userStr);
        if (userData && userData.id) {
          console.log('🔄 Usando usuário do localStorage:', userData);
          return userData;
        }
      }
    } catch (error) {
      console.error('❌ Erro ao parsear usuário do localStorage:', error);
    }
    
    return null;
  };
  const [fotos, setFotos] = useState<PropertyFoto[]>([]);
  const [imagemCapa, setImagemCapa] = useState<string>('');
  const [amenidadesSelecionadas, setAmenidadesSelecionadas] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [propertyData, setPropertyData] = useState<Property | null>(null);
  const [developments, setDevelopments] = useState<Development[]>([]);
  const [loadingDevelopments, setLoadingDevelopments] = useState(false);
  const [photoUploadingCount, setPhotoUploadingCount] = useState(0);

  // Estados para os campos do formulário
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    max_guests: '',
    bedrooms: '',
    bathrooms: '',
    property_type: 'apartment',
    check_in_time: '15:00',
    check_out_time: '11:00',
    house_rules: '',
    development_id: '',
    additional_info: '',
  });

  // Carregar empreendimentos (plano Porto 2 Life: apenas empreendimentos Porto 2 Life no combo)
  const isPorto2LifePlan = subscriptionPlan === 'porto2life';
  useEffect(() => {
    const loadDevelopments = async () => {
      try {
        setLoadingDevelopments(true);
        const allDevelopments = await DevelopmentService.getAllDevelopments();
        const filtered = isPorto2LifePlan
          ? allDevelopments.filter((d) => {
              const name = (d.name || '').toLowerCase();
              return name.includes('porto2life') || name.includes('porto 2 life') || name.includes('porto-2-life');
            })
          : allDevelopments;
        setDevelopments(filtered);
        if (!id && filtered.length === 1) {
          setFormData(prev => ({ ...prev, development_id: filtered[0].id.toString() }));
        }
      } catch (error) {
        console.error('Erro ao carregar empreendimentos:', error);
      } finally {
        setLoadingDevelopments(false);
      }
    };
    loadDevelopments();
  }, [isPorto2LifePlan]);

  // Carregar dados do imóvel se estiver em modo de edição
  useEffect(() => {
    if (id) {
      console.log('🔄 useEffect: Carregando dados do imóvel ID:', id);
      setIsEditing(true);
      loadPropertyData(parseInt(id));
    }
  }, [id]);

  const loadPropertyData = async (propertyId: number) => {
    try {
      setLoading(true);
      const property = await PropertyService.getPropertyByIdSimple(propertyId);
      setPropertyData(property);
      
      if (property.cover_image) {
        setImagemCapa(extractImageFilename(property.cover_image));
      }
      
      // Preencher formulário com dados do imóvel
      if (property) {
        setFormData({
          title: property.title || '',
          description: property.description || '',
          max_guests: property.max_guests?.toString() || '',
          bedrooms: property.bedrooms?.toString() || '',
          bathrooms: property.bathrooms?.toString() || '',
          property_type: property.property_type || 'apartment',
          check_in_time: property.check_in_time || '15:00',
          check_out_time: property.check_out_time || '11:00',
          house_rules: property.house_rules || '',
          development_id: property.development_id?.toString() || '',
          additional_info: property.additional_info || ''
        });

        // Preencher amenidades
        if (property.amenities) {
          try {
            const amenitiesArray = typeof property.amenities === 'string' 
              ? JSON.parse(property.amenities) 
              : property.amenities;
            setAmenidadesSelecionadas(amenitiesArray);
          } catch (error) {
            console.error('Erro ao parsear amenidades:', error);
            setAmenidadesSelecionadas([]);
          }
        }

        // Preencher fotos usando a nova tabela property_photos
        try {
          console.log('📸 Carregando fotos do imóvel ID:', propertyId);
          const propertyPhotos = await PropertyPhotoService.getActivePhotosByPropertyId(propertyId);
          console.log('📸 Fotos encontradas no banco:', propertyPhotos);
          
          if (propertyPhotos && propertyPhotos.length > 0) {
            const fotosWithCaptions: PropertyFoto[] = propertyPhotos.map(
              (photo: PropertyPhoto) => ({
                id: photo.id,
                filename: photo.filename,
                url: getPropertyImageUrl(propertyId, photo.filename),
                legenda: photo.caption || '',
              }),
            );
            console.log('📸 Fotos processadas do banco:', fotosWithCaptions);
            setFotos(fotosWithCaptions);
            setImagemCapa((prev) => {
              if (prev && fotosWithCaptions.some((f) => coverMatchesPhoto(prev, f))) {
                return prev;
              }
              if (property.cover_image) {
                return extractImageFilename(property.cover_image);
              }
              return fotosWithCaptions[0]
                ? extractImageFilename(fotosWithCaptions[0].filename)
                : '';
            });
          } else {
            // Se não há fotos no banco, não carregar nenhuma foto
            console.log('📸 Nenhuma foto encontrada no banco para o imóvel ID:', propertyId);
            setFotos([]);
          }
        } catch (error) {
          console.error('❌ Erro ao carregar fotos do imóvel:', error);
          // Em caso de erro, não carregar fotos padrão
          setFotos([]);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar dados do imóvel:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os dados do imóvel.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const numericFields = ['bedrooms', 'max_guests', 'bathrooms'] as const;

  const handleNumericChange = (field: (typeof numericFields)[number], value: string) => {
    handleInputChange(field, value.replace(/\D/g, ''));
  };

  const parseNumericField = (value: string, fallback = 1) => {
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
  };

  const resolveCoverFilename = (syncedFotos: PropertyFoto[]): string => {
    const cover = extractImageFilename(imagemCapa);
    if (!cover) {
      throw new Error('Selecione uma foto de capa antes de salvar.');
    }
    const index = fotos.findIndex((foto) => coverMatchesPhoto(cover, foto));
    if (index === -1) {
      throw new Error(
        'A imagem de capa selecionada não corresponde a nenhuma foto. Selecione uma das fotos acima.',
      );
    }
    const synced = syncedFotos[index];
    const filename = synced ? extractImageFilename(synced.filename) : '';
    if (!filename) {
      throw new Error('Não foi possível definir a foto de capa. Tente novamente.');
    }
    return filename;
  };

  const savePropertyPhotos = async (propertyId: number): Promise<string> => {
    const syncedFotos: PropertyFoto[] = [];

    for (let i = 0; i < fotos.length; i++) {
      const foto = fotos[i];

      if (foto.id) {
        await PropertyPhotoService.updatePhoto(foto.id, {
          caption: foto.legenda,
          display_order: i + 1,
        });
        syncedFotos.push(foto);
        continue;
      }

      if (!foto.file) {
        throw new Error(`Foto ${i + 1} inválida: arquivo não encontrado.`);
      }

      const uploaded = await PropertyPhotoService.uploadPhoto(
        propertyId,
        foto.file,
        foto.legenda,
      );

      await PropertyPhotoService.updatePhoto(uploaded.id, {
        display_order: i + 1,
      });

      syncedFotos.push({
        id: uploaded.id,
        url: getPropertyImageUrl(propertyId, uploaded.filename),
        legenda: foto.legenda,
        filename: uploaded.filename,
      });
    }

    const coverFilename = resolveCoverFilename(syncedFotos);
    await PropertyService.updateProperty(propertyId, { cover_image: coverFilename });
    return coverFilename;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Verificar se a autenticação ainda está carregando
    if (authLoading) {
      toast({
        title: "Aguarde",
        description: "Verificando autenticação...",
        variant: "default",
      });
      return;
    }
    
    // Validar campos obrigatórios
    if (!formData.title.trim()) {
      toast({
        title: "Título Obrigatório",
        description: "Por favor, preencha o nome do empreendimento.",
        variant: "destructive",
      });
      return;
    }
    
    // Validar descrição removendo tags HTML e verificando se há conteúdo real
    const descriptionText = formData.description.replace(/<[^>]*>/g, '').trim();
    if (!descriptionText || descriptionText === '') {
      toast({
        title: "Descrição Obrigatória",
        description: "Por favor, preencha a descrição do imóvel.",
        variant: "destructive",
      });
      return;
    }
    
    if (!formData.development_id?.trim()) {
      toast({
        title: "Empreendimento obrigatório",
        description: "Selecione o empreendimento. Endereço, cidade e estado vêm dos dados cadastrados no empreendimento.",
        variant: "destructive",
      });
      return;
    }

    if (!formData.bedrooms || !formData.bathrooms || !formData.max_guests) {
      toast({
        title: "Campos obrigatórios",
        description: "Informe quartos, banheiros e hóspedes (apenas números).",
        variant: "destructive",
      });
      return;
    }

    // Validar se há pelo menos uma foto cadastrada
    if (fotos.length === 0) {
      toast({
        title: "Fotos Obrigatórias",
        description: "Por favor, adicione pelo menos uma foto do imóvel antes de salvar.",
        variant: "destructive",
      });
      return;
    }
    
    if (!extractImageFilename(imagemCapa)) {
      toast({
        title: "Imagem de Capa Obrigatória",
        description: "Selecione uma foto como capa clicando em uma das miniaturas acima.",
        variant: "destructive",
      });
      return;
    }

    if (!fotos.some((foto) => coverMatchesPhoto(imagemCapa, foto))) {
      toast({
        title: "Imagem de Capa Inválida",
        description: "A capa selecionada não corresponde a nenhuma foto. Selecione uma das fotos listadas.",
        variant: "destructive",
      });
      return;
    }

    if (photoUploadingCount > 0) {
      toast({
        title: "Envio em andamento",
        description: "Aguarde as fotos terminarem de enviar para a API.",
        variant: "destructive",
      });
      return;
    }
    
    try {
      setLoading(true);
      
      if (isEditing && id) {
        // Atualizar imóvel existente
        const propertyId = parseInt(id);
        const propertyData = {
          title: formData.title,
          description: formData.description,
          max_guests: parseNumericField(formData.max_guests),
          bedrooms: parseNumericField(formData.bedrooms),
          bathrooms: parseNumericField(formData.bathrooms),
          property_type: formData.property_type,
          check_in_time: formData.check_in_time,
          check_out_time: formData.check_out_time,
          house_rules: formData.house_rules,
          additional_info: formData.additional_info,
          amenities: JSON.stringify(amenidadesSelecionadas),
          development_id: formData.development_id ? parseInt(formData.development_id) : null
        };
        
        await PropertyService.updateProperty(propertyId, propertyData);
        const coverFilename = await savePropertyPhotos(propertyId);
        setImagemCapa(coverFilename);
      } else {
        // Criar novo imóvel - SOLUÇÃO DEFINITIVA
        const currentUser = getCurrentUser();
        console.log('👤 Usuário obtido:', currentUser);
        
        if (!currentUser || !currentUser.id) {
          console.error('❌ Nenhum usuário válido encontrado');
          toast({
            title: "Erro de Autenticação",
            description: "Você precisa estar logado para cadastrar um imóvel. Faça login novamente.",
            variant: "destructive",
          });
          navigate('/login');
          return;
        }
        
        const propertyData = {
          title: formData.title,
          description: formData.description,
          latitude: -8.2865,
          longitude: -35.1185,
          max_guests: parseNumericField(formData.max_guests),
          bedrooms: parseNumericField(formData.bedrooms),
          bathrooms: parseNumericField(formData.bathrooms),
          property_type: formData.property_type,
          check_in_time: formData.check_in_time,
          check_out_time: formData.check_out_time,
          house_rules: formData.house_rules,
          additional_info: formData.additional_info,
          amenities: JSON.stringify(amenidadesSelecionadas),
          owner_id: currentUser.id,
          development_id: formData.development_id ? parseInt(formData.development_id) : null
        };
        
        const newProperty = await PropertyService.createProperty(propertyData);
        
        if (!newProperty?.id) {
          throw new Error('Erro ao criar imóvel: propriedade não foi criada corretamente');
        }
        
        await savePropertyPhotos(newProperty.id);
      }
      
      console.log('Dados do formulário:', {
        formData,
        amenidadesSelecionadas,
        fotos: fotos.map(foto => ({
          id: foto.id,
          filename: foto.url.includes('/') ? foto.url.split('/').pop() : foto.url,
          caption: foto.legenda
        }))
      });
      
      toast({
        title: isEditing ? "Imóvel atualizado!" : "Imóvel cadastrado!",
        description: isEditing ? "Seu anúncio foi atualizado com sucesso." : "Seu anúncio foi criado com sucesso.",
      });
      navigate("/minha-area/meus-imoveis");
    } catch (error) {
      console.error('Erro ao salvar imóvel:', error);
      const message =
        error instanceof Error
          ? error.message
          : PropertyPhotoService.formatUploadError(error);
      toast({
        title: "Erro ao salvar",
        description: message || "Erro ao salvar o imóvel. Tente novamente.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) return;

    const propertyId = id ? parseInt(id, 10) : null;

    for (const file of Array.from(files)) {
      if (!file.type.startsWith('image/')) {
        toast({
          title: "Arquivo inválido",
          description: `${file.name} não é uma imagem suportada.`,
          variant: "destructive",
        });
        continue;
      }

      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: "Arquivo grande demais",
          description: `${file.name} excede 10MB.`,
          variant: "destructive",
        });
        continue;
      }

      // Edição: envio imediato para livestay-backend (sem blob, sem site/public)
      if (propertyId) {
        setPhotoUploadingCount((c) => c + 1);
        try {
          const saved = await persistPropertyPhotoToApi(propertyId, file);
          setFotos((prev) => [...prev, saved]);
          setImagemCapa((prev) =>
            prev && extractImageFilename(prev)
              ? prev
              : extractImageFilename(saved.filename),
          );
          toast({
            title: "Foto salva na API",
            description: `${file.name} — ${saved.url}`,
          });
        } catch (error) {
          toast({
            title: "Falha no envio",
            description: PropertyPhotoService.formatUploadError(error),
            variant: "destructive",
          });
        } finally {
          setPhotoUploadingCount((c) => Math.max(0, c - 1));
        }
        continue;
      }

      // Cadastro novo: preview local; no Salvar → POST /api/upload + /api/property-photos
      const previewUrl = URL.createObjectURL(file);
      setFotos((prev) => [
        ...prev,
        {
          id: null,
          url: previewUrl,
          legenda: '',
          filename: file.name,
          file,
          pendingUpload: true,
        },
      ]);
      setImagemCapa((prev) =>
        prev && extractImageFilename(prev)
          ? prev
          : extractImageFilename(file.name),
      );
    }

    e.target.value = '';
  };

  const removeFoto = async (index: number) => {
    const foto = fotos[index];
    console.log('🗑️ Removendo foto:', { index, foto });
    
    // Se a foto tem ID, deletar do banco de dados
    if (foto.id) {
      try {
        console.log('🗑️ Deletando foto do banco com ID:', foto.id);
        await PropertyPhotoService.deletePhoto(foto.id);
        console.log('✅ Foto deletada do banco com sucesso');
      } catch (error) {
        console.error('❌ Erro ao deletar foto do banco:', error);
        toast({
          title: "Erro",
          description: "Erro ao deletar foto. Tente novamente.",
          variant: "destructive"
        });
        return;
      }
    } else {
      console.log('🗑️ Foto sem ID, removendo apenas da lista local');
    }
    
    setFotos((prev) => {
      const newFotos = prev.filter((_, i) => i !== index);
      console.log('🗑️ Fotos após remoção:', newFotos);
      return newFotos;
    });
    
    toast({
      title: "Foto removida",
      description: "A foto foi removida com sucesso.",
    });
  };

  const updateLegenda = async (index: number, legenda: string) => {
    const foto = fotos[index];
    
    // Atualizar no estado local
    setFotos((prev) =>
      prev.map((foto, i) => (i === index ? { ...foto, legenda } : foto))
    );
    
    // Se a foto tem ID, atualizar no banco de dados
    if (foto.id) {
      try {
        await PropertyPhotoService.updatePhotoCaption(foto.id, legenda);
      } catch (error) {
        console.error('Erro ao atualizar legenda no banco:', error);
        // Reverter a mudança local em caso de erro
        setFotos((prev) =>
          prev.map((foto, i) => (i === index ? { ...foto, legenda: foto.legenda } : foto))
        );
        toast({
          title: "Erro",
          description: "Erro ao salvar legenda. Tente novamente.",
          variant: "destructive"
        });
      }
    }
  };



  if (loading || authLoading) {
    return (
      <MinhaAreaPageShell contentClassName="container mx-auto px-4 py-8 max-w-4xl">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">
              {authLoading ? 'Verificando autenticação...' : 'Carregando dados do imóvel...'}
            </p>
          </div>
        </div>
      </MinhaAreaPageShell>
    );
  }

  return (
    <MinhaAreaPageShell contentClassName="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">
            {isEditing ? 'Editar Imóvel' : 'Cadastrar Novo Imóvel'}
          </h1>
          <p className="text-muted-foreground">
            {isEditing ? 'Atualize as informações do seu imóvel' : 'Preencha as informações do seu imóvel'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Informações Básicas */}
          <Card>
            <CardHeader>
              <CardTitle>Informações Básicas</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Empreendimento
                </label>
                <Select 
                  value={formData.development_id}
                  onValueChange={(value) => handleInputChange('development_id', value)}
                  disabled={loadingDevelopments}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={loadingDevelopments ? "Carregando..." : "Selecione o empreendimento"} />
                  </SelectTrigger>
                  <SelectContent>
                    {developments.map((development) => (
                      <SelectItem key={development.id} value={development.id.toString()}>
                        {development.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-2">
                  O endereço, a cidade e o estado do anúncio seguem o cadastro do empreendimento selecionado.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Nome do imóvel <span className="text-destructive">*</span>
                </label>
                <Input
                  type="text"
                  placeholder="Ex: Residencial Vista Linda"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  required
                />
    </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Descrição <span className="text-destructive">*</span>
                </label>
                <RichTextEditor
                  value={formData.description}
                  onChange={(value) => handleInputChange('description', value)}
                  placeholder="Descreva seu imóvel, suas características e diferenciais..."
                  minHeight="200px"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Use o editor para formatar o texto com negrito, itálico, listas e mais.
                </p>
    </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Quartos
                  </label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="1"
                    value={formData.bedrooms}
                    onChange={(e) => handleNumericChange('bedrooms', e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Banheiros
                  </label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="1"
                    value={formData.bathrooms}
                    onChange={(e) => handleNumericChange('bathrooms', e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Hóspedes
                  </label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="4"
                    value={formData.max_guests}
                    onChange={(e) => handleNumericChange('max_guests', e.target.value)}
                    required
                  />
                </div>
              </div>


            </CardContent>
          </Card>

          {/* Imagem de Capa */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                Imagem de Capa
                {!imagemCapa && <span className="text-destructive text-sm">* Obrigatório</span>}
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Selecione uma imagem que será exibida como capa do imóvel em todas as listagens
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {fotos.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {fotos.map((foto, index) => (
                    <div
                      key={foto.id ?? `cover-${foto.filename}-${index}`}
                      className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                        coverMatchesPhoto(imagemCapa, foto)
                          ? 'border-primary ring-2 ring-primary/20'
                          : 'border-border hover:border-primary/50'
                      }`}
                      onClick={() => setImagemCapa(getPhotoFilename(foto))}
                    >
                      <img
                        src={foto.url}
                        alt={`Foto ${index + 1}`}
                        className="w-full h-24 object-cover"
                      />
                      {coverMatchesPhoto(imagemCapa, foto) && (
                        <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                          <div className="bg-primary text-primary-foreground rounded-full p-1">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
    </div>
    </div>
                      )}
    </div>
                  ))}
    </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <p>Adicione fotos primeiro para selecionar uma imagem de capa</p>
    </div>
              )}
              
              {fotos.length > 0 && !imagemCapa && (
                <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
                  <p className="text-destructive text-sm font-medium">
                    ⚠️ Selecione uma imagem de capa para continuar
                  </p>
    </div>
              )}
            </CardContent>
          </Card>

          {/* Fotos */}
          <Card>
            <CardHeader>
              <CardTitle>Fotos do Imóvel</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Upload de novas fotos */}
              <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
                <input
                  type="file"
                  id="upload"
                  multiple
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageUpload}
                />
                <label
                  htmlFor="upload"
                  className="cursor-pointer flex flex-col items-center gap-2"
                >
                  <Upload className="h-12 w-12 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    Clique para adicionar fotos ou arraste aqui
                  </p>
                  <p className="text-xs text-muted-foreground">
                    JPG, PNG ou WEBP (máx. 10MB cada)
                  </p>
                </label>
    </div>


              {fotos.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {fotos.map((foto, index) => (
                    <div
                      key={foto.id ?? `photo-${foto.filename}-${index}`}
                      className="relative group border rounded-lg overflow-hidden"
                    >
                      <img
                        src={foto.url}
                        alt={`Foto ${index + 1}`}
                        className="w-full h-48 object-cover"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => removeFoto(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                      <div className="p-2">
                        <Input
                          type="text"
                          placeholder="Adicionar legenda..."
                          value={foto.legenda}
                          onChange={(e) => updateLegenda(index, e.target.value)}
                          className="text-sm"
                        />
    </div>
    </div>
                  ))}
    </div>
              )}
            </CardContent>
          </Card>

          {/* Amenidades */}
          <Card>
            <CardHeader>
              <CardTitle>O que este lugar oferece</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {amenidades.map((amenidade) => {
                  const Icon = amenidade.icon;
                  const isSelected = amenidadesSelecionadas.includes(amenidade.id);
                  
                  return (
                    <div
                      key={amenidade.id}
                      className={`flex items-center space-x-3 p-3 border rounded-lg transition-colors cursor-pointer ${
                        isSelected 
                          ? 'bg-primary/10 border-primary' 
                          : 'hover:bg-accent border-border'
                      }`}
                      onClick={() => {
                        if (isSelected) {
                          setAmenidadesSelecionadas(prev => prev.filter(id => id !== amenidade.id));
                        } else {
                          setAmenidadesSelecionadas(prev => [...prev, amenidade.id]);
                        }
                      }}
                    >
                      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                        isSelected 
                          ? 'bg-primary border-primary' 
                          : 'border-muted-foreground'
                      }`}>
                        {isSelected && (
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
    </div>
                      <Icon className="h-5 w-5 text-muted-foreground" />
                      <span className="text-sm font-medium flex-1">
                        {amenidade.label}
                      </span>
    </div>
                  );
                })}
    </div>
            </CardContent>
          </Card>

          {/* Regras */}
          <Card>
            <CardHeader>
              <CardTitle>Regras</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Horário de Check-in
                  </label>
                  <Input
                    type="time"
                    placeholder="14:00"
                    value={formData.check_in_time}
                    onChange={(e) => handleInputChange('check_in_time', e.target.value)}
                    required
                  />
    </div>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Horário de Check-out
                  </label>
                  <Input
                    type="time"
                    placeholder="11:00"
                    value={formData.check_out_time}
                    onChange={(e) => handleInputChange('check_out_time', e.target.value)}
                    required
                  />
    </div>
    </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Regras da Casa
                </label>
                <RichTextEditor
                  value={formData.house_rules}
                  onChange={(value) => handleInputChange('house_rules', value)}
                  placeholder="Ex: • Check-in: 15:00 às 22:00
• Check-out: até 11:00
• Não é permitido fumar
• Não é permitido animais de estimação
• Respeite o silêncio após 22:00
• Mantenha o imóvel limpo e organizado
• Não é permitido festas ou eventos
• Use os equipamentos com cuidado
• Em caso de problemas, entre em contato conosco"
                  minHeight="200px"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Use o editor para formatar o texto com negrito, itálico, listas e mais.
                </p>
    </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Informações Adicionais
                </label>
                <RichTextEditor
                  value={formData.additional_info}
                  onChange={(value) => handleInputChange('additional_info', value)}
                  placeholder="Adicione informações adicionais sobre o imóvel..."
                  minHeight="240px"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Use o editor para formatar o texto com negrito, itálico, listas e mais.
                </p>
    </div>
            </CardContent>
          </Card>


          <div className="flex gap-4">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={() => navigate("/minha-area/meus-imoveis")}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="flex-1 gap-2"
              disabled={loading || photoUploadingCount > 0}
            >
              <Save className="h-4 w-4" />
              {photoUploadingCount > 0
                ? 'Enviando fotos para API...'
                : loading
                  ? 'Salvando...'
                  : isEditing
                    ? 'Atualizar Imóvel'
                    : 'Salvar Imóvel'}
            </Button>
          </div>
        </form>
    </MinhaAreaPageShell>
  );
};

export default NewProperty;
